import { hc } from "hono/client";
import type {
  User,
  AgentInfo,
  Agent,
  AgentConfig,
  AgentAction,
  AgentStatus,
  Conversation,
  DocumentResponse,
  ConversationMessageResponse,
  BrandKit,
  TenantUsage,
  CreateActionResponse,
  DeleteActionResponse,
  VoiceAgent,
  IntegrationConnection,
  TenantInfo
} from "./schema";
import type { WhatsAppVoiceAgent } from "@/types/whatsappVoiceAgent";
import type { AppType } from ".";
import type { ApiBuildAgentConfig } from "../../types/buildAgent";
import { setCookie } from "../utils";

// NOTE: can only be run on the client
const { api } = hc<AppType>("/", {
  init: {
    credentials: "include"
  }
});

export interface ApiError {
  status: number;
  error: string;
}

export default api;

export const getUser = async () => {
  const res = await api.account.v1.user
    .$get()
    .then((res) => res.json() as Promise<User | ApiError>);
  if ("status" in res && res.status === 401) {
    window.location.href = "/login";
    return;
  }
  return res;
};

export const getTenant = async () => {
  const res = await api.account.v1.user.tenants.current
    .$get()
    .then((res) => res.json() as Promise<TenantInfo>);
  console.log(res);
  if (res.id) {
    window.tenantId = res.id;
    // 保存到 cookie 而不是 localStorage
    setCookie("tenant_id", res.id, {
      path: "/",
      maxAge: 30 * 24 * 60 * 60, // 30 天
      sameSite: "lax"
    });
  }
  return res;
};

export const getAgents = async () => {
  const res = await api.mapping.v1.agents
    .$get({
      query: {
        tenant_id: window.tenantId
      }
    })
    .then(
      (res) => res.json() as Promise<{ agentsList: AgentInfo[] } | ApiError>
    );
  if ("status" in res && res.status === 401) {
    window.location.href = "/login";
    return;
  }
  return res;
};

export const getAgent = async (id: string) => {
  const res = await api.mapping.v1.agents[":id"]
    .$get({
      param: { id }
    })
    .then((res) => res.json() as Promise<Agent | ApiError>);
  if ("status" in res && res.status === 401) {
    window.location.href = "/login";
    return;
  }
  return res;
};

export const saveAgent = async (id: string, json: { [key: string]: any }) => {
  return await api.mapping.v1.agents[":id"]
    .$put({
      param: { id },
      // @ts-ignore
      json
    })
    .then((res) => res.json() as Promise<Agent>);
};

export const getAgentConfig = async (id: string) => {
  const res = await api.mapping.v1.agents[":id"].config
    .$get({
      param: { id }
    })
    .then((res) => res.json() as Promise<AgentConfig>);
  if (res.config) {
    localStorage.setItem(`agent-${id}`, JSON.stringify(res.config));
  }
  return res;
};

export const getAgentActions = async (id: string) => {
  return await api.mapping.v1.agents[":id"].actions
    .$get({
      param: { id }
    })
    .then((res) => res.json() as Promise<AgentAction[] | ApiError>);
};

export const createMeetingAction = async (
  tenant_id: string,
  agent_id: string,
  action: {
    message: string;
    ui_type: string;
    button_text: string;
    cta_url: string;
  }
) => {
  return await api.integration.action.meetings
    .$post({
      // @ts-ignore
      json: {
        tenant_id,
        agent_id,
        action
      }
    })
    .then((res) => res.json() as Promise<any>);
};

export const getMeetingActions = async (action_ids: string[]) => {
  return await api.integration.action.meetings
    .$get({
      query: {
        action_ids: action_ids
      }
    })
    .then(
      (res) =>
        res.json() as Promise<
          { actions: AgentAction[]; message: string } | ApiError
        >
    );
};

export const updateMeetingAction = async (
  tenant_id: string,
  agent_id: string,
  action: {
    message: string;
    ui_type: string;
    button_text: string;
    cta_url: string;
    action_id: string;
  }
) => {
  return await api.integration.action.meetings
    .$put({
      // @ts-ignore
      json: {
        tenant_id,
        agent_id,
        action
      }
    })
    .then((res) => res.json() as Promise<any>);
};

export const deleteMeetingAction = async (
  tenant_id: string,
  agent_id: string,
  action_id: string
) => {
  return await api.integration.action.meetings
    .$delete({
      // @ts-ignore
      json: {
        tenant_id,
        agent_id,
        action_id
      }
    })
    .then((res) => res.json() as Promise<any>);
};

export const deleteAction = async (action_id: string) => {
  return await (api.integration as any).actions
    .$delete({
      query: {
        action_id
      },
      // @ts-ignore
      json: {
        action_id
      }
    })
    .then((res) => res.json() as Promise<DeleteActionResponse>);
};

export const createAction = async (tenant_id: string, name: string) => {
  return await (api.integration as any).actions
    .$post({
      // @ts-ignore
      json: {
        tenant_id,
        name
      }
    })
    .then((res) => res.json() as Promise<CreateActionResponse>);
};

export const publishAgent = async (id: string) => {
  return await api.mapping.v1.agents[":id"].publish
    .$post({
      param: { id },
      // @ts-ignore
      json: {
        update_config: false
      }
    })
    .then(
      (res) =>
        res.json() as Promise<{
          agent_id: string;
          message: string;
          status: AgentStatus;
          error: string;
        }>
    );
};

export const signOut = async () => {
  return await api.account.v1.logout
    .$post({})
    .then((res) => res.json() as Promise<any>);
};

export const getConversations = async (
  id: string,
  source = "all",
  start_time: string = "",
  end_time: string = ""
) => {
  return await api.conversation.v1.conversations
    .$get({
      query: {
        agent_id: id,
        source,
        start_time,
        end_time
      }
    })
    .then(
      (res) =>
        res.json() as Promise<null | {
          conversations: Conversation[];
          error: string;
        }>
    );
};

export const getDocuments = async (
  dataset_id: string,
  skip: number = 0,
  limit: number = 5,
  search = "",
  status?: string
) => {
  return await api.rag.v1.datasets[":dataset_id"].documents
    .$get({
      param: {
        dataset_id
      },
      // @ts-ignore
      query: {
        limit,
        skip,
        search,
        ...(status && { status })
      }
    })
    .then((res) => res.json() as Promise<DocumentResponse>);
};

export const getIntegrations = async () => {
  const res = await api.integration.connections
    .$get({
      query: {
        tenant_id: window.tenantId
      }
    })
    .then(
      (res) =>
        res.json() as Promise<{
          connections: IntegrationConnection[];
        }>
    );
  if (res.connections) {
    return res.connections;
  }
  return [];
};

export const getMessages = async (id: string, source: string) => {
  return await api.conversation.v1.conversations[":id"].messages
    .$get({
      param: {
        id
      },
      // @ts-ignore
      query: {
        source
      }
    })
    .then((res) => res.json() as Promise<null | ConversationMessageResponse>);
};

export const getBrandKit = async (id: string) => {
  return await api.mapping.v1.agents[":id"].brandkit
    .$get({
      param: {
        id
      }
    })
    .then((res) => res.json() as Promise<BrandKit>);
};

export const postBrandKit = async (id: string, json: BrandKit) => {
  return await api.mapping.v1.agents[":id"].brandkit
    .$post({
      param: {
        id
      },
      // @ts-ignore
      json
    })
    .then((res) => res.json() as Promise<BrandKit>);
};

export const saveBrandKit = async (id: string, json: BrandKit) => {
  return await api.mapping.v1.agents[":id"].brandkit
    .$put({
      param: {
        id
      },
      // @ts-ignore
      json
    })
    .then((res) => res.json() as Promise<BrandKit & { error: string }>);
};

// New API methods for BuildAgent configuration
export const getBuildAgentConfig = async (id: string) => {
  const res = await api.mapping.v1.agents[":id"].config
    .$get({
      param: { id }
    })
    .then(
      (res) =>
        res.json() as Promise<{
          id?: string;
          agent_id: string;
          config: ApiBuildAgentConfig;
          created_at?: string;
          updated_at?: string;
        }>
    );
  return res;
};

export const saveBuildAgentConfig = async (
  id: string,
  config: ApiBuildAgentConfig
) => {
  return await api.mapping.v1.agents[":id"].config
    .$put({
      param: { id },
      // @ts-ignore
      json: {
        config
      }
    })
    .then(
      (res) =>
        res.json() as Promise<{
          id: string;
          agent_id: string;
          config: ApiBuildAgentConfig;
          created_at: string;
          updated_at: string;
        }>
    );
};

export const getUsage = async () => {
  return await api.usage.v1.usage[":tenant_id"]
    .$get({
      param: {
        tenant_id: window.tenantId
      }
    })
    .then((res) => res.json() as Promise<{ config: TenantUsage }>);
};

export const getVoiceConfig = async (id: string) => {
  const res = await api.mapping.v1.agents[":id"].voice
    .$get({
      param: { id }
    })
    .then((res) => res.json() as Promise<VoiceAgent>);
  if (res.id) {
    return res;
  }
  return null;
};

// WATI Integration APIs
export const getAgentApiKey = async (agent_id: string) => {
  const res = await api.conversation.v1.wati[":agent_id"].api_key
    .$get({
      param: { agent_id },
      // @ts-ignore
      query: {
        tenant_id: window.tenantId
      }
    })
    .then((res) => res.json() as Promise<{ token?: string; error?: string }>);
  return res;
};

export const saveWatiSettings = async (params: {
  agent_id: string;
  wati_endpoint: string;
  wati_user_token: string;
  human_agent_email?: string;
}) => {
  const { agent_id, wati_endpoint, wati_user_token, human_agent_email } =
    params;
  const res = await api.conversation.v1.wati.settings
    .$post({
      // @ts-ignore
      json: {
        tenant_id: window.tenantId,
        agent_id,
        wati_endpoint,
        wati_user_token,
        human_agent_email
      }
    })
    .then(
      (res) => res.json() as Promise<{ success?: boolean; message?: string }>
    );
  return res;
};

export const getWatiSettings = async (agent_id: string) => {
  const res = await api.conversation.v1.wati.settings
    .$get({
      query: { agent_id: agent_id }
    })
    .then(
      (res) =>
        res.json() as Promise<{
          wati_endpoint?: string;
          wati_user_token?: string;
          human_agent_email?: string;
        }>
    );
  return res;
};

// WhatsApp Voice Agent APIs
export const getWhatsappVoiceAgent = async (
  tenant_id: string,
  text_agent_id: string
): Promise<WhatsAppVoiceAgent> => {
  return await api.agents["by-text-agent"]
    .$get({
      query: {
        tenant_id,
        text_agent_id
      }
    })
    .then((res) => res.json() as Promise<WhatsAppVoiceAgent>);
};

export const createWhatsappVoiceAgent = async (
  tenant_id: string,
  text_agent_id: string
): Promise<WhatsAppVoiceAgent> => {
  return await api.agents["quick-create"]
    .$post({
      // @ts-ignore
      json: {
        tenant_id,
        text_agent_id
      }
    })
    .then((res) => res.json() as Promise<WhatsAppVoiceAgent>);
};

export const updateWhatsappVoiceAgent = async (
  id: string,
  updateData: Partial<
    Pick<
      WhatsAppVoiceAgent,
      "agent_name" | "instruction" | "agent_config" | "disabled"
    >
  >
): Promise<WhatsAppVoiceAgent> => {
  return await api.agents[":id"]
    .$put({
      param: { id },
      // @ts-ignore
      json: updateData
    })
    .then((res) => res.json() as Promise<WhatsAppVoiceAgent>);
};
